import {installInteractionAnalytics} from './interactionAnalytics';
import {installTwinAnalytics} from './twinAnalytics';

declare global {
  interface Window {
    gtag: (...args: unknown[]) => void;
    dataLayer: unknown[];
    loadGoogleAnalytics: () => void;
  }
}
const GA_MEASUREMENT_ID = 'G-2HQKRW5GY2';
let loaded=false, lastPath='', stopCollectors: (()=>void)|undefined;
export const hasAnalyticsConsent=():boolean=>{
  try{return localStorage.getItem('cookie-consent')==='accepted';}catch{return false;}
};
const cleanLocation=()=>location.origin+location.pathname;
export function trackEvent(eventName:string,params?:Record<string,string|number|boolean>):void{
  if(!hasAnalyticsConsent()||typeof window.gtag!=='function')return;
  window.gtag('event',eventName,{page_path:location.pathname,...params,page_location:cleanLocation()});
}
export function trackPageView(path:string,title?:string):void{
  if(!hasAnalyticsConsent())return;
  const clean=path.split(/[?#]/)[0];
  if(clean===lastPath)return;
  lastPath=clean;
  trackEvent('page_view',{page_path:clean,page_title:title||document.title});
}
function startCollectors(){
  if(stopCollectors)return;
  const docs=new Map<Document,()=>void>();
  const frames=new Map<HTMLIFrameElement,()=>void>();
  const attach=(doc:Document)=>{
    if(docs.has(doc))return;
    const collect=(name:string,params?:Record<string,string|number|boolean>)=>trackEvent(name,{...params,surface:doc===document?'website':location.pathname.slice(1)});
    const stop=installInteractionAnalytics(doc,collect);
    const stopTwin=doc===document?()=>{}:installTwinAnalytics(doc,collect);
    docs.set(doc,()=>{stop();stopTwin();});
  };
  attach(document);
  const scan=()=>{
    for(const [frame,off] of frames)if(!frame.isConnected){off();frames.delete(frame);}
    document.querySelectorAll('iframe').forEach(frame=>{
      if(frames.has(frame))return;
      let current:Document|null=null;
      const onLoad=()=>{try{
        if(current){docs.get(current)?.();docs.delete(current);}
        current=frame.contentDocument;
        if(current&&frame.contentWindow?.location.origin===location.origin)attach(current);
      }catch{/* Cross-origin players require their own API integration. */}};
      frame.addEventListener('load',onLoad);frames.set(frame,()=>{frame.removeEventListener('load',onLoad);if(current){docs.get(current)?.();docs.delete(current);}});onLoad();
    });
  };
  const observer=new MutationObserver(scan);observer.observe(document.body,{childList:true,subtree:true});scan();
  stopCollectors=()=>{observer.disconnect();frames.forEach(off=>off());docs.forEach(off=>off());frames.clear();docs.clear();stopCollectors=undefined;};
}
function loadGoogleAnalytics(){
  if(!hasAnalyticsConsent())return;
  (window as unknown as Record<string,unknown>)['ga-disable-'+GA_MEASUREMENT_ID]=false;
  if(!loaded){
    loaded=true;
    const script=document.createElement('script');script.async=true;script.src='https://www.googletagmanager.com/gtag/js?id='+GA_MEASUREMENT_ID;
    document.head.appendChild(script);
    window.gtag('js',new Date());
    window.gtag('config',GA_MEASUREMENT_ID,{send_page_view:false,page_location:cleanLocation(),page_referrer:document.referrer?new URL(document.referrer).origin:''});
  }
  startCollectors();trackPageView(location.pathname);
}
export function initializeAnalytics(){
  window.dataLayer=window.dataLayer||[];
  // gtag queues Arguments objects; replacing this with a rest array changes its command protocol.
  // eslint-disable-next-line prefer-rest-params
  window.gtag=window.gtag||function(){window.dataLayer.push(arguments);};
  window.loadGoogleAnalytics=loadGoogleAnalytics;
  loadGoogleAnalytics();
  window.addEventListener('storage',e=>{if(e.key==='cookie-consent'){if(hasAnalyticsConsent())loadGoogleAnalytics();else disableAnalytics();}});
}
function disableAnalytics(){
  (window as unknown as Record<string,unknown>)['ga-disable-'+GA_MEASUREMENT_ID]=true;
  stopCollectors?.();lastPath='';
}
export function setAnalyticsConsent(accepted:boolean){
  try{localStorage.setItem('cookie-consent',accepted?'accepted':'declined');localStorage.setItem('cookie-consent-date',new Date().toISOString());}catch{return;}
  if(accepted)loadGoogleAnalytics();else disableAnalytics();
}
export const trackLinkClick=(linkUrl:string,_linkText:string,category='navigation')=>{
  const url=new URL(linkUrl,location.origin);
  trackEvent('link_click',{link_url:['http:','https:'].includes(url.protocol)?url.origin+url.pathname:url.protocol,event_category:category});
};
export const trackCTAClick=(ctaName:string,location:string)=>trackEvent('cta_click',{cta_name:ctaName,cta_location:location});
export const trackFormSubmit=(formName:string,success:boolean)=>trackEvent('form_submit',{form_name:formName,success});
